/**
 * SecurityTree — The tree component that wires useSecurityTree to react-arborist.
 *
 * Responsibilities:
 * - Converts flat Map<string, TreeNode> to nested data for react-arborist
 * - Passes toggle handler via onActivate
 * - Provides search/filter through react-arborist's searchTerm
 * - Renders the virtualized tree
 */

import { useMemo, useRef } from 'react';
import { Tree, type TreeApi } from 'react-arborist';
import type { UseSecurityTreeReturn } from './useSecurityTree';
import { SecurityTreeNode, type SecurityTreeNodeData } from './SecurityTreeNode';
import type { TreeNode } from './types';

interface SecurityTreeProps {
  /** The hook return value. */
  treeState: UseSecurityTreeReturn;
  /** Height of the tree container in pixels. */
  height?: number;
  /** Optional search/filter term for text matching. */
  searchTerm?: string;
}

/**
 * Convert the flat tree map to nested data that react-arborist expects.
 * Only includes nodes that are visible per the current filter.
 */
function toNestedData(
  tree: Map<string, TreeNode>,
  visibleNodeIds: Set<string>,
  roots: TreeNode[],
): SecurityTreeNodeData[] {
  function buildNode(node: TreeNode): SecurityTreeNodeData | null {
    if (!visibleNodeIds.has(node.id)) return null;

    const children: SecurityTreeNodeData[] = [];
    for (const childId of node.childIds) {
      const child = tree.get(childId);
      if (child) {
        const nested = buildNode(child);
        if (nested) children.push(nested);
      }
    }

    return {
      id: node.id,
      name: node.label,
      nodeType: node.nodeType,
      accessState: node.accessState,
      children: children.length > 0 ? children : undefined,
    };
  }

  const result: SecurityTreeNodeData[] = [];
  for (const root of roots) {
    const nested = buildNode(root);
    if (nested) result.push(nested);
  }
  return result;
}

export function SecurityTree({ treeState, height = 500, searchTerm }: SecurityTreeProps) {
  const { tree, roots, visibleNodeIds, toggle } = treeState;
  const treeRef = useRef<TreeApi<SecurityTreeNodeData>>(null);

  const data = useMemo(
    () => toNestedData(tree, visibleNodeIds, roots),
    [tree, visibleNodeIds, roots],
  );

  return (
    <Tree<SecurityTreeNodeData>
      ref={treeRef}
      data={data}
      openByDefault={true}
      width="100%"
      height={height}
      indent={24}
      rowHeight={32}
      overscanCount={5}
      disableDrag={true}
      disableDrop={true}
      disableEdit={true}
      disableMultiSelection={true}
      searchTerm={searchTerm}
      searchMatch={(node, term) =>
        node.data.name.toLowerCase().includes(term.toLowerCase())
      }
      onActivate={(node) => {
        // All states are clickable — users click parent nodes to set entire subtrees
        toggle(node.data.id);
      }}
    >
      {SecurityTreeNode}
    </Tree>
  );
}
